import React, { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import {
	getPdgmGroupingEstimateOptions,
	OasisAddendumStatusEnum,
	OasisAssessmentTypeEnum,
	PdgmAdmissionSourceEnum,
	PdgmAdmissionSourceOptions,
	PdgmTimingEnum,
	PdgmTimingOptions,
	type FormFieldModel,
	type OasisAddendumViewModel,
	type OasisAssessmentViewModel
} from '@generated';
import { Section } from '~/shared/section/Section';
import { SectionHeader } from '~/shared/section/SectionHeader';
import { Badge } from '~/shared/components/Badge';
import { Callout } from '~/shared/components/Callout';
import type { StatusIntents } from '~/theme/variants';
import { ButtonGroup } from '~/shared/forms/controls/base-controls/ButtonGroup';

const PDGM_ASSESSMENT_TYPES = [OasisAssessmentTypeEnum.StartOfCare, OasisAssessmentTypeEnum.RecertFollowUp, OasisAssessmentTypeEnum.ROC];

const FUNCTIONAL_ITEM_LABELS: Record<string, string> = {
	M1800: 'Grooming',
	M1810: 'Dress Upper Body',
	M1820: 'Dress Lower Body',
	M1830: 'Bathing',
	M1840: 'Toilet Transferring',
	M1850: 'Transferring',
	M1860: 'Ambulation / Locomotion',
	M1033: 'Hospitalization Risk'
};

export function OasisTabPdgm({ oasisAssessment }: { oasisAssessment: OasisAssessmentViewModel }) {
	return (
		<div className="space-y-6">
			<PdgmEstimateSection assessment={oasisAssessment} />
			<AddendumsSection assessment={oasisAssessment} />
		</div>
	);
}

function PdgmEstimateSection({ assessment }: { assessment: OasisAssessmentViewModel }) {
	const [timingId, setTimingId] = useState<number>(PdgmTimingEnum.Early);
	const [admissionSourceId, setAdmissionSourceId] = useState<number>(PdgmAdmissionSourceEnum.Community);
	const { data: estimate } = useSuspenseQuery(getPdgmGroupingEstimateOptions(assessment.id, timingId, admissionSourceId));

	return (
		<Section>
			<div className="mb-4 flex min-h-[30px] items-center justify-between">
				<div className="flex items-center gap-3">
					<h3 className="font-semibold uppercase tracking-wider text-gray-500">PDGM Grouping</h3>
					<Badge intent="warning">Estimate</Badge>
				</div>
				<span className="text-xs text-gray-400">
					{assessment.oasisAssessmentTypeLabel} · {assessment.oasisAssessmentDefinition.name} ·{' '}
					{assessment.oasisAssessmentDefinition.version}
				</span>
			</div>
			<p className="mb-4 text-xs text-gray-400">
				Estimated from OASIS responses and active diagnoses. Timing and admission source are your selections, not claims data — this is not a
				billed HIPPS.
			</p>
			<div className="mb-6 flex flex-wrap items-end gap-6">
				<EstimateToggle label="Timing" options={PdgmTimingOptions} value={timingId} onChange={setTimingId} />
				<EstimateToggle
					label="Admission Source"
					options={PdgmAdmissionSourceOptions}
					value={admissionSourceId}
					onChange={setAdmissionSourceId}
				/>
			</div>

			{estimate.groupable ? (
				<div className="space-y-6">
					<div className="grid grid-cols-2 gap-4 rounded-md border border-gray-200 bg-gray-50 p-4 sm:grid-cols-3 lg:grid-cols-6">
						<EstimateStat label="HIPPS" value={<span className="font-mono">{estimate.hippsCode}</span>} />
						<EstimateStat label="Clinical Group" value={estimate.clinicalGroup} detail={estimate.clinicalGroupLabel} />
						<EstimateStat label="Functional Level" value={estimate.functionalLevel} detail={`${estimate.functionalPoints} pts`} />
						<EstimateStat label="Comorbidity" value={estimate.comorbidityLevel} />
						<EstimateStat label="Case-Mix Weight" value={estimate.caseMixWeight?.toFixed(4)} />
						<EstimateStat label="LUPA Threshold" value={estimate.lupaThreshold} detail="visits" />
					</div>
					<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
						<div>
							<SectionHeader title="Functional Scoring" />
							<table className="w-full text-xs">
								<thead>
									<tr className="border-b border-gray-200 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400">
										<th className="py-1.5 pr-2">Item</th>
										<th className="py-1.5 pr-2">Response</th>
										<th className="py-1.5 text-right">Points</th>
									</tr>
								</thead>
								<tbody>
									{estimate.functionalItemScores.map(item => (
										<tr key={item.oasisItem} className="border-b border-gray-100 last:border-0">
											<td className="py-1.5 pr-2">
												<span className="font-mono text-gray-400">{item.oasisItem}</span>
												<span className="ml-2 text-gray-700">{FUNCTIONAL_ITEM_LABELS[item.oasisItem] ?? ''}</span>
											</td>
											<td className="py-1.5 pr-2 text-gray-500">{item.response ?? '—'}</td>
											<td className="py-1.5 text-right font-semibold text-gray-700">{item.points}</td>
										</tr>
									))}
									<tr>
										<td className="py-2 font-semibold text-gray-600" colSpan={2}>
											Total
										</td>
										<td className="py-2 text-right font-bold text-gray-900">{estimate.functionalPoints}</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div className="space-y-6">
							<div>
								<SectionHeader title="Comorbidity Adjustment" />
								{estimate.comorbidityDetails.length > 0 ? (
									<ul className="space-y-1 text-xs text-gray-600">
										{estimate.comorbidityDetails.map(detail => (
											<li key={detail}>{detail}</li>
										))}
									</ul>
								) : (
									<p className="text-xs text-gray-400">No comorbidity adjustment from the secondary diagnoses.</p>
								)}
							</div>
							{estimate.issues.length > 0 && <EstimateIssues issues={estimate.issues} />}
						</div>
					</div>
				</div>
			) : (
				<Callout title="Unable to estimate PDGM grouping" subtitle="The assessment is missing information the grouper requires.">
					{estimate.issues.length > 0 && (
						<div className="mx-auto max-w-lg text-left">
							<EstimateIssues issues={estimate.issues} />
						</div>
					)}
				</Callout>
			)}
		</Section>
	);
}

function EstimateToggle({
	label,
	options,
	value,
	onChange
}: {
	label: string;
	options: { id: number; label: string }[];
	value: number;
	onChange: (id: number) => void;
}) {
	return (
		<div>
			<p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
			<ButtonGroup options={options} isSelected={(id: number) => value === id} onSelect={(id: number) => onChange(id)} />
		</div>
	);
}

function EstimateStat({ label, value, detail }: { label: string; value: React.ReactNode; detail?: string | null }) {
	return (
		<div>
			<p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
			<p className="mt-0.5 text-lg font-bold text-gray-900">{value ?? '—'}</p>
			{detail && <p className="text-[11px] text-gray-500">{detail}</p>}
		</div>
	);
}

const ADDENDUM_STATUS_INTENTS: Record<number, StatusIntents> = {
	[OasisAddendumStatusEnum.NeedsResponse]: 'warning',
	[OasisAddendumStatusEnum.ResponseAccepted]: 'success',
	[OasisAddendumStatusEnum.ResponseRejected]: 'destructive'
};

function AddendumsSection({ assessment }: { assessment: OasisAssessmentViewModel }) {
	const addendums = assessment.addendums ?? [];
	const hasInProgress = addendums.some(x => x.oasisAddendumStatusId === OasisAddendumStatusEnum.NeedsResponse);

	return (
		<Section>
			<div className="mb-4 flex min-h-[30px] items-center justify-between">
				<div className="flex items-center gap-3">
					<h3 className="font-semibold uppercase tracking-wider text-gray-500">Addendums</h3>
					{hasInProgress && <Badge intent="warning">In Progress</Badge>}
				</div>
			</div>
			{addendums.length === 0 ? (
				<p className="text-xs text-gray-400">No addendums for this assessment.</p>
			) : (
				<div className="space-y-4">
					{assessment.addendums.map(addendum => (
						<AddendumDetails key={addendum.id} addendum={addendum} />
					))}
				</div>
			)}
		</Section>
	);
}

function AddendumDetails({ addendum }: { addendum: OasisAddendumViewModel }) {
	const inProgress = addendum.oasisAddendumStatusId === OasisAddendumStatusEnum.NeedsResponse;
	const changes = getAddendumValueChanges(addendum);
	const labels = getFieldLabels(addendum.oasisTask.oasisDefinition.documentationFormFields);

	return (
		<div className={`rounded-md border p-4 ${inProgress ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200'}`}>
			<div className="flex items-center justify-between gap-3">
				<p className="text-sm font-semibold text-gray-700">{addendum.oasisTask.oasisDefinition.title}</p>
				<Badge intent={ADDENDUM_STATUS_INTENTS[addendum.oasisAddendumStatusId] ?? 'neutral'}>{addendum.oasisAddendumStatusLabel}</Badge>
			</div>
			<p className="mt-1 text-xs text-gray-500">{addendum.divergenceReason}</p>
			{addendum.rejectionReason && <p className="mt-1 text-xs text-red-700">Rejected: {addendum.rejectionReason}</p>}
			{changes.length > 0 && (
				<table className="mt-3 w-full text-xs">
					<thead>
						<tr className="border-b border-gray-200 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-400">
							<th className="py-1.5 pr-2">Item</th>
							<th className="py-1.5 pr-2">Original</th>
							<th className="py-1.5">Addendum</th>
						</tr>
					</thead>
					<tbody>
						{changes.map(change => (
							<tr key={change.name} className="border-b border-gray-100 last:border-0">
								<td className="py-1.5 pr-2">
									<span className="font-mono text-gray-400">{change.name}</span>
									{labels[change.name] && <span className="ml-2 text-gray-700">{labels[change.name]}</span>}
								</td>
								<td className="py-1.5 pr-2 text-gray-500">{change.original ?? '—'}</td>
								<td className="py-1.5 font-semibold text-gray-700">{change.updated ?? '—'}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	);
}

function originalInitialValues(addendum: OasisAddendumViewModel) {
	if (!addendum.collaborationTask?.values) {
		throw new Error('Addendum must have collaboration task values to compare.');
	}
	const previousReplacement = addendum.collaborationTask.documentationValuesHistory.find(x => x.replacedByOasisAddendumId === addendum.id);
	if (previousReplacement) {
		return previousReplacement.values;
	}
	return addendum.collaborationTask.values;
}

function getAddendumValues(addendum: OasisAddendumViewModel) {
	if (addendum.collaborationTask) {
		if (!addendum.collaborationTask.values) {
			throw new Error('Addendum must have collaboration task values to compare.');
		}
		return addendum.collaborationTask.values;
	} else if (!addendum.officeReviewValues) {
		throw new Error('Addendum must have office review values to compare.');
	}
	return addendum.officeReviewValues;
}
function getAddendumValueChanges(addendum: OasisAddendumViewModel) {
	if (!addendum.collaborationTask?.values) {
		throw new Error('Addendum must have collaboration task values to compare.');
	}
	const baseline = originalInitialValues(addendum);
	const updated = getAddendumValues(addendum);

	return Object.keys(updated)
		.filter(name => (updated[name] ?? '') !== (baseline[name] ?? ''))
		.map(name => ({ name, original: baseline[name], updated: updated[name] }));
}

function getFieldLabels(fields: FormFieldModel[], labels: Record<string, string> = {}): Record<string, string> {
	for (const field of fields) {
		if (field.label) {
			labels[field.name] = field.label;
		}
		if (field.formFields) {
			getFieldLabels(field.formFields, labels);
		}
	}
	return labels;
}

function EstimateIssues({ issues }: { issues: string[] }) {
	return (
		<div className="rounded-md border border-amber-200 bg-amber-50 p-3">
			<ul className="space-y-1.5">
				{issues.map(issue => (
					<li key={issue} className="flex items-start gap-2 text-xs text-amber-800">
						<AlertCircle size={13} className="mt-0.5 shrink-0" />
						{issue}
					</li>
				))}
			</ul>
		</div>
	);
}
